import { setCookie, deleteCookie, isInsecureLocalhost } from "@huub-web/utils";

/**
 * Cookie max age in seconds (7 days).
 * Matches typical JWT expiration.
 */
const COOKIE_MAX_AGE = 7 * 24 * 60 * 60;

type SetAuthCookieParams = {
  /**
   * The identity token to store
   */
  idToken: string;
  /**
   * Cookie name (e.g. "huub_id_token")
   */
  name: string;
  /**
   * Cookie domain (e.g. ".huub.nl" for production, undefined for localhost)
   */
  domain?: string;
};

type DeleteAuthCookieParams = {
  /**
   * Cookie name
   */
  name: string;
  /**
   * Cookie domain
   */
  domain?: string;
};

/**
 * Sets the authentication cookie with the id_token.
 * Cookie is configured for cross-domain sharing between marketing site and app.
 */
export function setAuthCookie({
  idToken,
  name,
  domain,
}: SetAuthCookieParams): void {
  // The cookie carries the id_token (the de-facto session credential), so it
  // must NEVER travel over plain HTTP. Previously `Secure` was gated on a
  // cookie domain being configured (`!!domain`), which meant a misconfigured or
  // empty domain in a deployed env would ship the JWT cookie insecurely
  // (SMA-1711). Always set `Secure`, except on plain-HTTP localhost where the
  // browser would otherwise drop the cookie during local development.
  const isSecure = !isInsecureLocalhost();

  setCookie(name, idToken, {
    domain: domain || undefined,
    path: "/",
    secure: isSecure,
    // Kept as `lax` (not `strict`) on purpose: this cookie is a one-time
    // cross-domain handoff from the marketing site to the app, which a
    // `strict` policy would block on the top-level navigation.
    sameSite: "lax" as const,
    maxAge: COOKIE_MAX_AGE,
  });
}

/**
 * Deletes the authentication cookie.
 * Must use same domain/path as when setting.
 */
export function deleteAuthCookie({
  name,
  domain,
}: DeleteAuthCookieParams): void {
  deleteCookie(name, {
    domain: domain || undefined,
    path: "/",
  });
}
